/**
 * useErrorPatterns — Shared learner-error-pattern derivation.
 *
 * Previously lived in `components/progress/ErrorPatternsSection.tsx` as a
 * UI-co-located hook. Moved here (audit DDD-3 / P2, ronde 2 2026) because
 * the mascot domain (`FloatingDjay`) also consumes it, and mascot should
 * not depend on a progress-page UI module.
 *
 * Pure derivation from `AppState.familyMastery` — no rendering, no side
 * effects. The progress UI re-exports this hook from its own file for
 * backwards compatibility.
 */
import { useMemo } from 'react'
import { AppState } from '../app/types'
import { GroupId } from '../config/systemConfig'
import { MODULES } from '../content'
import { getModuleFromQuestionId } from './activityHelpers'
import { FAMILY_DISPLAY_NAMES } from '../content/familyLabels'

// Adapted from differentiationInsights.ts for learner view
const ERROR_TYPE_RECOMMENDATIONS: Record<string, string> = {
  // Clock errors
  HOUR_OFF: 'Let goed op de kleine wijzer. Welk uur is het net geweest?',
  MINUTE_OFF_5:
    'Kijk nog eens goed naar de grote wijzer. Tel in sprongetjes van 5!',
  MINUTE_OFF_10:
    'Kijk nog eens goed naar de grote wijzer. Tel in sprongetjes van 5!',
  MINUTE_OFF_15:
    'Verwar je kwart voor en kwart over? Kwart over is op de 3, kwart voor is op de 9.',
  HALF_CONFUSION:
    'Half betekent dat de grote wijzer op de 6 staat, precies tussen twee uren in.',
  FORMAT_24H:
    "Let op of het overdag (12-uurs) of 's middags/'s avonds (24-uurs) is.",
  BOTH_OFF:
    'Neem de tijd om eerst naar het uur te kijken, en dan pas naar de minuten.',
  // Money errors
  TOO_LOW:
    'Je bedrag is een beetje te laag. Heb je alle munten en biljetten geteld?',
  TOO_HIGH: 'Je bedrag is een beetje te hoog. Probeer gepast te betalen!',
  TOO_LOW_5C: 'Vergeet je niet een muntje van 5 cent toe te voegen?',
  TOO_HIGH_5C: 'Heb je misschien een muntje van 5 cent te veel gebruikt?',
  TOO_LOW_10C: 'Vergeet je niet een muntje van 10 cent toe te voegen?',
  TOO_HIGH_10C: 'Heb je misschien een muntje van 10 cent te veel gebruikt?',
  TOO_LOW_50C: 'Vergeet je niet een muntje van 50 cent toe te voegen?',
  TOO_HIGH_50C: 'Heb je misschien een muntje van 50 cent te veel gebruikt?',
  TOO_LOW_100C: 'Vergeet je niet een hele euro toe te voegen?',
  TOO_HIGH_100C: 'Heb je misschien een euro te veel gebruikt?',
  INVALID_DENOM:
    'Gebruik alleen de munten en biljetten die je mag gebruiken in deze opdracht.',
  TOO_FEW_TOKENS: 'Je hebt te weinig munten of biljetten gebruikt.',
  TOO_MANY_TOKENS:
    'Probeer met zo min mogelijk munten en biljetten te betalen!',
  MISSING_REQUIRED_BILL: 'Vergeet niet het verplichte biljet te gebruiken.',
  // Spelling/Grammar
  STAM_T:
    'Denk aan de smurfenregel! Ik smurf, hij smurft. Hoor je een t? Dan schrijf je stam+t.',
  D_T: 'Maak het woord langer om te horen of het op een d of t eindigt (hond - honden).',
  EI_IJ: 'Twijfel je tussen ei en ij? Denk aan het ei-verhaal!',
  AU_OU: 'Twijfel je tussen au en ou? Denk aan het au-verhaal!',
}

function humanizeFamilyId(familyId: string): string {
  const parts = familyId.split('-').slice(1)
  return parts
    .join(' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase())
}

export interface ErrorPattern {
  errorType: string
  count: number
  familyId: string
  familyName: string
  moduleIcon: string
  moduleColor: string
  recommendation: string
}

export function useErrorPatterns(
  state: AppState,
  selectedGroup: GroupId,
): ErrorPattern[] {
  return useMemo(() => {
    if (!state.familyMastery) return []
    const groupSuffix = `_g${selectedGroup}`
    const errorCounts: Record<
      string,
      {
        count: number
        familyId: string
      }
    > = {}
    Object.entries(state.familyMastery).forEach(([key, familyState]) => {
      if (!key.endsWith(groupSuffix) || !familyState.history) return
      const familyId = familyState.familyId
      familyState.history.forEach((attempt) => {
        if (!attempt.isCorrect && attempt.errorType) {
          const errorType = attempt.errorType
          if (!errorCounts[errorType]) {
            errorCounts[errorType] = { count: 0, familyId }
          }
          errorCounts[errorType].count++
          errorCounts[errorType].familyId = familyId
        }
      })
    })
    return Object.entries(errorCounts)
      .filter(([_, data]) => data.count >= 1)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 3)
      .map(([errorType, data]) => {
        const baseErrorType = errorType.replace(/_\d+$/, '')
        let recommendation =
          'Neem even de tijd om de uitleg goed te lezen als je een foutje maakt.'
        if (ERROR_TYPE_RECOMMENDATIONS[errorType]) {
          recommendation = ERROR_TYPE_RECOMMENDATIONS[errorType]
        } else if (ERROR_TYPE_RECOMMENDATIONS[baseErrorType]) {
          recommendation = ERROR_TYPE_RECOMMENDATIONS[baseErrorType]
        }
        const moduleId = getModuleFromQuestionId(data.familyId)
        const moduleDef = MODULES.find((m) => m.id === moduleId)
        const familyName =
          FAMILY_DISPLAY_NAMES[data.familyId] || humanizeFamilyId(data.familyId)
        return {
          errorType,
          count: data.count,
          familyId: data.familyId,
          familyName,
          moduleIcon: moduleDef?.icon || '💡',
          moduleColor: moduleDef?.color || 'bg-violet-500',
          recommendation,
        }
      })
  }, [state.familyMastery, selectedGroup])
}
